import { AppBar, Toolbar, Typography, IconButton, Box, Avatar, Button } from '@mui/material'
import {
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Storage as DatabaseIcon,
  Dashboard as DashboardIcon,
  Receipt as AnalyserIcon,
  AdminPanelSettings as AdminIcon,
  Assignment as POIcon,
} from '@mui/icons-material'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'

interface HeaderProps {
  onOpenSettings: () => void
  appName: string
}

export default function Header({ onOpenSettings, appName }: HeaderProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuth()

  const initials = user?.fullName
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'U'

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  return (
    <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'primary.main' }}>
      <Toolbar variant="dense">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: '#fff' }}>
            {appName}
          </Typography>
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            color="inherit"
            startIcon={<DashboardIcon />}
            onClick={() => navigate('/')}
            variant={location.pathname === '/' ? 'outlined' : 'text'}
            sx={{ borderColor: 'rgba(255,255,255,0.5)', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
          >
            Dashboard
          </Button>
          <Button
            color="inherit"
            startIcon={<AnalyserIcon />}
            onClick={() => navigate('/invoice-analyser')}
            variant={location.pathname === '/invoice-analyser' ? 'outlined' : 'text'}
            sx={{ borderColor: 'rgba(255,255,255,0.5)', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
          >
            Invoice Analyser
          </Button>
          <Button
            color="inherit"
            startIcon={<DatabaseIcon />}
            onClick={() => navigate('/database')}
            variant={location.pathname === '/database' ? 'outlined' : 'text'}
            sx={{ borderColor: 'rgba(255,255,255,0.5)', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
          >
            Database
          </Button>
          <Button
            color="inherit"
            startIcon={<POIcon />}
            onClick={() => navigate('/po-tracking')}
            variant={location.pathname === '/po-tracking' ? 'outlined' : 'text'}
            sx={{ borderColor: 'rgba(255,255,255,0.5)', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
          >
            PO Tracking
          </Button>
          {user?.role === 'admin' && (
            <Button
              color="inherit"
              startIcon={<AdminIcon />}
              onClick={() => navigate('/admin')}
              variant={location.pathname === '/admin' ? 'outlined' : 'text'}
              sx={{ borderColor: 'rgba(255,255,255,0.5)', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
            >
              Admin
            </Button>
          )}
        </Box>

        {user && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'right', mr: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {user.fullName}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                {user.username}
              </Typography>
            </Box>
            <Avatar sx={{ width: 36, height: 36, bgcolor: 'secondary.main' }}>{initials}</Avatar>
          </Box>
        )}

        <IconButton color="inherit" onClick={onOpenSettings} title="Settings">
          <SettingsIcon />
        </IconButton>

        <IconButton color="inherit" onClick={handleLogout} title="Logout">
          <LogoutIcon />
        </IconButton>
      </Toolbar>
    </AppBar>
  )
}
